import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { getUser, type UserListItem } from '../users.api';

export type UserRecordState =
  { status: 'loading' } | { status: 'loaded'; user: UserListItem } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): UserRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** One office user, fetched by id and re-fetchable after a mutation commits. */
export function useUserRecord(id: string) {
  const [state, setState] = useState<UserRecordState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getUser(id)
      .then((user) => {
        if (!cancelled) setState({ status: 'loaded', user });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', user: await getUser(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, reload };
}
